import { useState } from 'react'

// HelpButton is the "?" in the topbar. It opens an overview so a first-time operator
// understands what xnapz is, what each menu section does, and the dense domain terms —
// the onboarding the UI otherwise lacks.
export function HelpButton() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button className="help-btn" onClick={() => setOpen(true)} title="What is xnapz?">
        ?
      </button>
      {open && <HelpOverlay onClose={() => setOpen(false)} />}
    </>
  )
}

function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div className="help-overlay" onClick={onClose}>
      <div className="help-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="About xnapz">
        <div className="help-head">
          <h2>What is xnapz?</h2>
          <button className="x" onClick={onClose} aria-label="close">
            ✕
          </button>
        </div>

        <p>
          xnapz is an event-driven message-bus platform for orchestrating autonomous
          AI-agent software development: tickets are decomposed into a task DAG, each task is
          auto-assigned to the best-matching AI skill (or deferred to a human), executed, and
          the outcomes feed back so future assignments get smarter.
        </p>

        <p className="help-story-link">
          New here?{' '}
          <a
            href="#/story"
            onClick={onClose}
          >
            See how a job travels the ladder →
          </a>
        </p>

        <h3>The menu</h3>
        <ul className="help-list">
          <li>
            <strong>Plan</strong> — design the work: a chat-driven task graph (Planning), saved
            plans, and DAG runs (Orchestration).
          </li>
          <li>
            <strong>Operate</strong> — run &amp; route messages: inboxes, category rollups,
            routing rules, and schedules.
          </li>
          <li>
            <strong>Observe</strong> — watch the live system: health, traces, the connection
            topology, and loop-safety budgets. These views auto-refresh (look for the
            <span className="live-inline"> ● live</span> badge).
          </li>
          <li>
            <strong>Configure</strong> — the skill catalog (searchable) and which task types you
            subscribe to.
          </li>
        </ul>

        <h3>Key terms</h3>
        <dl className="help-terms">
          <dt>Blast radius</dt>
          <dd>how far a message's effects can reach; large radius gates risky actions.</dd>
          <dt>Autonomy</dt>
          <dd>how aggressively a recipient may act without human approval.</dd>
          <dt>Accuracy ↔ budget</dt>
          <dd>the 5-step lever trading model cost for predicted success.</dd>
          <dt>Dead-letter</dt>
          <dd>messages that exhausted delivery and need attention.</dd>
          <dt>Loop-safety budget</dt>
          <dd>depth/execution bounds that defer a workflow to a human before it can loop.</dd>
        </dl>
      </div>
    </div>
  )
}
